import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  MenuItem,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Chip,
  Divider,
  Collapse,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PublicIcon from "@mui/icons-material/Public";
import StorageIcon from "@mui/icons-material/Storage";
import ScienceIcon from "@mui/icons-material/Science";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import clinicalTrialsService from "../../services/clinicalTrialsService";
import { useNavigate } from "react-router-dom";
import EditIcon from "@mui/icons-material/Edit";
import EligibilityPlainText from "../health/EligibilityPlainText";
import AddIcon from "@mui/icons-material/Add";

// City to IATA airport code mapper
const CITY_TO_IATA = {
  boston: "BOS",
  "boston ma": "BOS",
  "boston, ma": "BOS",
  "new york": "JFK",
  "new york ny": "JFK",
  "new york, ny": "JFK",
  nyc: "JFK",
  "los angeles": "LAX",
  "los angeles ca": "LAX",
  la: "LAX",
  chicago: "ORD",
  "chicago il": "ORD",
  "chicago, il": "ORD",
  houston: "IAH",
  "houston tx": "IAH",
  philadelphia: "PHL",
  "philadelphia pa": "PHL",
  phoenix: "PHX",
  "phoenix az": "PHX",
  "san antonio": "SAT",
  "san diego": "SAN",
  dallas: "DFW",
  "dallas tx": "DFW",
  "san francisco": "SFO",
  "san francisco ca": "SFO",
  seattle: "SEA",
  "seattle wa": "SEA",
  denver: "DEN",
  "denver co": "DEN",
  washington: "DCA",
  "washington dc": "DCA",
  nashville: "BNA",
  "nashville tn": "BNA",
  baltimore: "BWI",
  "baltimore md": "BWI",
  minneapolis: "MSP",
  "minneapolis mn": "MSP",
  rochester: "RST",
  "rochester mn": "RST",
  "rochester, mn": "RST",
  "mayo clinic": "RST",
  cleveland: "CLE",
  "cleveland oh": "CLE",
  portland: "PDX",
  "portland or": "PDX",
  atlanta: "ATL",
  "atlanta ga": "ATL",
  miami: "MIA",
  "miami fl": "MIA",
  orlando: "MCO",
  "orlando fl": "MCO",
  tampa: "TPA",
  "tampa fl": "TPA",
  pittsburgh: "PIT",
  "pittsburgh pa": "PIT",
  detroit: "DTW",
  "detroit mi": "DTW",
  "st louis": "STL",
  "saint louis": "STL",
  "kansas city": "MCI",
  "salt lake city": "SLC",
  "new orleans": "MSY",
  indianapolis: "IND",
  columbus: "CMH",
  "columbus oh": "CMH",
  charlotte: "CLT",
  "charlotte nc": "CLT",
  raleigh: "RDU",
  durham: "RDU",
  "chapel hill": "RDU",
  richmond: "RIC",
  "richmond va": "RIC",
  sacramento: "SMF",
  "san jose": "SJC",
  tucson: "TUS",
  albuquerque: "ABQ",
  omaha: "OMA",
  buffalo: "BUF",
  hartford: "BDL",
  "new haven": "HVN",
  "ann arbor": "DTW",
  stanford: "SFO",
  "palo alto": "SFO",
  bethesda: "DCA",
  nih: "DCA",
  memphis: "MEM",
  louisville: "SDF",
  birmingham: "BHM",
  "birmingham al": "BHM",
  jacksonville: "JAX",
  austin: "AUS",
  "austin tx": "AUS",
};

const getBestTrialLocation = (trial) => {
  const locations = trial.locations;
  if (!locations || locations.length === 0) return null;

  const locationList = Array.isArray(locations) ? locations : [locations];

  for (const loc of locationList) {
    const locStr = (
      typeof loc === "string" ? loc : loc?.city || loc?.name || ""
    ).toLowerCase();
    for (const [city, iata] of Object.entries(CITY_TO_IATA)) {
      if (locStr.includes(city)) {
        const displayCity =
          typeof loc === "string" ? loc : loc?.city || loc?.name || loc;
        return { iata, city: displayCity };
      }
    }
  }
  return null;
};

const buildSanogridUrl = (trial) => {
  const sanogridBase =
    process.env.REACT_APP_SANOGRID_URL || "http://localhost:3001";
  const location = getBestTrialLocation(trial);

  const params = new URLSearchParams();
  params.set("trialId", trial.nctId || trial.id || "");
  params.set("trialName", trial.title || "");

  if (location) {
    params.set("destination", location.iata);
    params.set("city", location.city);
  }

  if (trial.startDate) params.set("startDate", trial.startDate);
  if (trial.completionDate) params.set("endDate", trial.completionDate);
  if (trial.sponsor) params.set("sponsor", trial.sponsor);

  return `${sanogridBase}/search?${params.toString()}`;
};

const ClinicalTrialsSearch = () => {
  const navigate = useNavigate();
  // Source selection: 'external', 'internal', or 'both'
  const [searchSource, setSearchSource] = useState("both");

  // Common search params
  const [searchParams, setSearchParams] = useState({
    query: "",
    status: "",
    pageSize: 10,
    ckdStage: "",
    targetOrgan: "",
  });

  const [externalTrials, setExternalTrials] = useState([]);
  const [internalTrials, setInternalTrials] = useState([]);
  const [allTrials, setAllTrials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [externalCount, setExternalCount] = useState(0);
  const [internalCount, setInternalCount] = useState(0);

  // Load internal trials on mount
  useEffect(() => {
    loadInitialInternalTrials();
  }, []);

  const loadInitialInternalTrials = async () => {
    try {
      console.log("Loading initial internal trials...");
      const response = await clinicalTrialsService.getAllInternalTrials();
      console.log("Initial internal trials response:", response);

      if (response.success) {
        const trialsWithSource = response.data.trials.map((trial) => ({
          ...trial,
          source: "internal",
        }));
        setInternalTrials(trialsWithSource);
        setInternalCount(response.data.totalCount);
        console.log(`Loaded ${trialsWithSource.length} internal trials`);
      }
    } catch (err) {
      console.error("Error loading initial internal trials:", err);
    }
  };

  const handleSourceChange = (event, newSource) => {
    if (newSource !== null) {
      setSearchSource(newSource);
      setError(null);
      // Clear previous results when switching sources
      setExternalTrials([]);
      setInternalTrials([]);
      setAllTrials([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSearchParams((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();

    // Only require query for external search
    if (!searchParams.query.trim() && searchSource === "external") {
      setError("Please enter a search term");
      return;
    }

    setLoading(true);
    setError(null);
    setExternalTrials([]);
    setInternalTrials([]);
    setAllTrials([]);

    console.log("Search params:", searchParams);
    console.log("Search source:", searchSource);

    try {
      if (searchSource === "external" || searchSource === "both") {
        await searchExternalTrials();
      }

      if (searchSource === "internal" || searchSource === "both") {
        await searchInternalTrials();
      }
    } catch (err) {
      setError(
        "Error performing search. Make sure the backend is running on http://localhost:8080"
      );
      console.error("Search error:", err);
    } finally {
      setLoading(false);
    }
  };

  const searchExternalTrials = async () => {
    try {
      console.log("Searching external trials with query:", searchParams.query);

      const response = await clinicalTrialsService.searchExternalTrials({
        condition: searchParams.query,
        status: searchParams.status,
        pageSize: searchParams.pageSize,
      });

      console.log("External search response:", response);

      if (response.success) {
        const trialsWithSource = response.data.trials.map((trial) => ({
          ...trial,
          source: "external",
        }));
        setExternalTrials(trialsWithSource);
        setExternalCount(response.data.totalCount);

        console.log(`Found ${trialsWithSource.length} external trials`);

        if (searchSource === "both") {
          setAllTrials((prev) => [...prev, ...trialsWithSource]);
        }
      } else {
        setError((prev) =>
          prev
            ? `${prev}\nExternal: ${response.message}`
            : `External API: ${response.message}`
        );
      }
    } catch (err) {
      console.error("External search error:", err);
      setError((prev) =>
        prev
          ? `${prev}\nFailed to search ClinicalTrials.gov`
          : "Failed to search ClinicalTrials.gov"
      );
    }
  };

  const searchInternalTrials = async () => {
    try {
      console.log("Searching internal trials...");
      console.log("Query:", searchParams.query);
      console.log("Filters:", {
        ckdStage: searchParams.ckdStage,
        status: searchParams.status,
        targetOrgan: searchParams.targetOrgan,
      });

      let response;

      // Check if using filters
      const hasFilters = searchParams.ckdStage || searchParams.targetOrgan;

      if (hasFilters) {
        // Use filter API
        console.log("Using filter API");
        response = await clinicalTrialsService.filterInternalTrials({
          ckdStage: searchParams.ckdStage,
          status: searchParams.status,
          targetOrgan: searchParams.targetOrgan,
        });
      } else if (searchParams.query && searchParams.query.trim()) {
        // Use keyword search
        console.log("Using keyword search with:", searchParams.query);
        response = await clinicalTrialsService.searchInternalTrials(
          searchParams.query
        );
      } else {
        // Get all trials
        console.log("Getting all internal trials");
        response = await clinicalTrialsService.getAllInternalTrials();
      }

      console.log("Internal search response:", response);

      if (response.success) {
        const trialsWithSource = response.data.trials.map((trial) => ({
          ...trial,
          source: "internal",
        }));
        setInternalTrials(trialsWithSource);
        setInternalCount(response.data.totalCount);

        console.log(`Found ${trialsWithSource.length} internal trials`);

        if (searchSource === "both") {
          setAllTrials((prev) => [...prev, ...trialsWithSource]);
        }
      } else {
        setError((prev) =>
          prev
            ? `${prev}\nInternal: ${response.message}`
            : `Internal DB: ${response.message}`
        );
      }
    } catch (err) {
      console.error("Internal search error:", err);
      setError((prev) =>
        prev
          ? `${prev}\nFailed to search internal database: ${err.message}`
          : `Failed to search internal database: ${err.message}`
      );
    }
  };

  const handleClearFilters = () => {
    setSearchParams({
      query: "",
      status: "",
      pageSize: 10,
      ckdStage: "",
      targetOrgan: "",
    });
    setExternalTrials([]);
    setInternalTrials([]);
    setAllTrials([]);
    setError(null);
  };

  // Get trials to display based on source
  const getDisplayTrials = () => {
    if (searchSource === "both") {
      return allTrials;
    } else if (searchSource === "external") {
      return externalTrials;
    } else {
      return internalTrials;
    }
  };

  const displayTrials = getDisplayTrials();
  const totalCount =
    searchSource === "both"
      ? externalCount + internalCount
      : searchSource === "external"
      ? externalCount
      : internalCount;

  return (
    <Container maxWidth="xl">
      {/* Add this section at the top */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4"> </Typography>

        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => navigate("/admin/trials/new")}
          sx={{
            background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
            fontWeight: "bold",
            px: 3,
          }}
        >
          Create New Trial
        </Button>
      </Box>

      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}

        <Typography
          variant="h3"
          component="h1"
          align="center"
          gutterBottom
          sx={{
            fontWeight: "bold",
            color: "#2c3e50",
          }}
        >
          Clinical Trials Search
        </Typography>
        <Typography
          variant="h6"
          align="center"
          color="text.secondary"
          sx={{ mb: 4 }}
        >
          Search from external, our curated database, or both
        </Typography>

        {/* Minimal Source Selector with Chips */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            justifyContent: "center",
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            Search Source:
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Chip
              icon={<PublicIcon />}
              label="External db"
              onClick={() => handleSourceChange(null, "external")}
              color={searchSource === "external" ? "primary" : "default"}
              variant={searchSource === "external" ? "filled" : "outlined"}
              clickable
            />
            <Chip
              icon={<StorageIcon />}
              label="Our Database"
              onClick={() => handleSourceChange(null, "internal")}
              color={searchSource === "internal" ? "success" : "default"}
              variant={searchSource === "internal" ? "filled" : "outlined"}
              clickable
            />
            <Chip
              icon={<AllInclusiveIcon />}
              label="Both Sources"
              onClick={() => handleSourceChange(null, "both")}
              color={searchSource === "both" ? "warning" : "default"}
              variant={searchSource === "both" ? "filled" : "outlined"}
              clickable
            />
          </Box>

          <Typography variant="caption" color="text.secondary">
            {searchSource === "external" &&
              "• Live data from ClinicalTrials.gov"}
            {searchSource === "internal" &&
              `• ${internalCount} curated trials in our database`}
            {searchSource === "both" &&
              "• Comprehensive search across all sources"}
          </Typography>
        </Box>

        {/* Unified Search Form */}
        <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
          <form onSubmit={handleSearch}>
            <Grid container spacing={3}>
              {/* Row 1: Query and Status */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={
                    searchSource === "external"
                      ? "Condition or Disease"
                      : "Search Term"
                  }
                  name="query"
                  value={searchParams.query}
                  onChange={handleInputChange}
                  placeholder={
                    searchSource === "external"
                      ? "e.g., amyloidosis, AL amyloidosis, cardiac amyloidosis"
                      : "e.g., cardiac, Mayo, daratumumab, Phase 3"
                  }
                  variant="outlined"
                  helperText={
                    searchSource === "internal"
                      ? "Search in title, description, sponsor, or leave blank to see all"
                      : "Enter a medical condition"
                  }
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Trial Status"
                  name="status"
                  value={searchParams.status}
                  onChange={handleInputChange}
                  variant="outlined"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="RECRUITING">Recruiting</MenuItem>
                  <MenuItem value="Recruiting">Recruiting (Internal)</MenuItem>
                  <MenuItem value="ACTIVE_NOT_RECRUITING">
                    Active, Not Recruiting
                  </MenuItem>
                  <MenuItem value="Active, not recruiting">
                    Active, Not Recruiting (Internal)
                  </MenuItem>
                  <MenuItem value="COMPLETED">Completed</MenuItem>
                  <MenuItem value="Completed">Completed (Internal)</MenuItem>
                  <MenuItem value="NOT_YET_RECRUITING">
                    Not Yet Recruiting
                  </MenuItem>
                </TextField>
              </Grid>

              {/* Row 2: Internal-specific filters (conditional) */}
              {(searchSource === "internal" || searchSource === "both") && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="CKD Stage (Internal Filter)"
                      name="ckdStage"
                      value={searchParams.ckdStage}
                      onChange={handleInputChange}
                      variant="outlined"
                    >
                      <MenuItem value="">All Types</MenuItem>
                      <MenuItem value="1">Stage 1</MenuItem>
                      <MenuItem value="2">Stage 2</MenuItem>
                      <MenuItem value="3a">Stage 3a</MenuItem>
                      <MenuItem value="3b">Stage 3b</MenuItem>
                      <MenuItem value="4">Stage 4</MenuItem>
                      <MenuItem value="5">Stage 5</MenuItem>
                      <MenuItem value="5D">Stage 5D</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      select
                      label="Target Organ (Internal Filter)"
                      name="targetOrgan"
                      value={searchParams.targetOrgan}
                      onChange={handleInputChange}
                      variant="outlined"
                    >
                      <MenuItem value="">All Organs</MenuItem>
                      <MenuItem value="Cardiac">Cardiac</MenuItem>
                      <MenuItem value="Renal">Renal</MenuItem>
                      <MenuItem value="Neurological">Neurological</MenuItem>
                      <MenuItem value="Multi-organ">Multi-organ</MenuItem>
                    </TextField>
                  </Grid>
                </>
              )}

              {/* Row 3: Page Size (conditional for external/both) */}
              {(searchSource === "external" || searchSource === "both") && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label="Results Per Page (External)"
                    name="pageSize"
                    value={searchParams.pageSize}
                    onChange={handleInputChange}
                    variant="outlined"
                  >
                    <MenuItem value={5}>5</MenuItem>
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={20}>20</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                  </TextField>
                </Grid>
              )}

              {/* Add spacer when only page size is shown to maintain button alignment */}
              {searchSource === "external" && <Grid item xs={12} md={6} />}

              {/* Row 4: Search and Clear Buttons */}
              <Grid item xs={12} md={6}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  color={
                    searchSource === "external"
                      ? "primary"
                      : searchSource === "internal"
                      ? "success"
                      : "warning"
                  }
                  startIcon={
                    loading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <SearchIcon />
                    )
                  }
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                  }}
                >
                  {loading
                    ? "Searching..."
                    : `Search ${
                        searchSource === "both"
                          ? "Both Sources"
                          : searchSource === "external"
                          ? "ClinicalTrials.gov"
                          : "Database"
                      }`}
                </Button>
              </Grid>

              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  color="inherit"
                  onClick={handleClearFilters}
                  sx={{
                    py: 1.5,
                    fontSize: "1.1rem",
                  }}
                >
                  Clear Filters
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>

        {/* Debug Info */}
        {process.env.NODE_ENV === "development" && (
          <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: "#f5f5f5" }}>
            <Typography variant="caption" display="block">
              <strong>Search results:</strong>
            </Typography>
            <Typography variant="caption" display="block">
              Search Source: {searchSource} | External Count: {externalCount} |
              Internal Count: {internalCount} | Display Trials:{" "}
              {displayTrials.length}
            </Typography>
            <Typography variant="caption" display="block">
              External Trials: {externalTrials.length} | Internal Trials:{" "}
              {internalTrials.length} | All Trials: {allTrials.length}
            </Typography>
          </Paper>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, whiteSpace: "pre-line" }}>
            {error}
          </Alert>
        )}

        {/* Results Summary */}
        {displayTrials.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                bgcolor:
                  searchSource === "both"
                    ? "#fff3e0"
                    : searchSource === "external"
                    ? "#e3f2fd"
                    : "#e8f5e9",
              }}
            >
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography
                    variant="h6"
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    {searchSource === "both" && (
                      <AllInclusiveIcon color="warning" />
                    )}
                    {searchSource === "external" && (
                      <PublicIcon color="primary" />
                    )}
                    {searchSource === "internal" && (
                      <StorageIcon color="success" />
                    )}
                    <span>
                      Found <strong>{totalCount}</strong> total trials - Showing{" "}
                      <strong>{displayTrials.length}</strong> results
                    </span>
                  </Typography>
                </Grid>

                {searchSource === "both" && (
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-end",
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        icon={<PublicIcon />}
                        label={`External: ${externalCount}`}
                        color="primary"
                        size="small"
                      />
                      <Chip
                        icon={<StorageIcon />}
                        label={`Internal: ${internalCount}`}
                        color="success"
                        size="small"
                      />
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Box>
        )}

        {/* Trial Cards */}
        {displayTrials.length > 0 && (
          <Grid container spacing={3}>
            {displayTrials.map((trial, index) => (
              <Grid
                item
                xs={12}
                key={`${trial.source}-${trial.nctId || trial.id}-${index}`}
              >
                <TrialCard trial={trial} />
              </Grid>
            ))}
          </Grid>
        )}

        {/* No Results */}
        {!loading && displayTrials.length === 0 && !error && (
          <Paper elevation={2} sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No trials found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try a different search term or adjust your filters
            </Typography>
            {searchSource === "internal" && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                💡 Tip: Leave the search field blank and click "Search Database"
                to see all {internalCount} trials
              </Typography>
            )}
          </Paper>
        )}
      </Box>
    </Container>
  );
};

// Trial Card Component
export const TrialCard = ({ trial }) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    const statusUpper = status?.toUpperCase() || "";

    if (statusUpper.includes("RECRUITING")) return "success";
    if (statusUpper.includes("ACTIVE")) return "warning";
    if (statusUpper.includes("COMPLETED")) return "info";
    if (statusUpper.includes("NOT YET")) return "error";

    return "default";
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, " ") || "Unknown";
  };

  const handleExpandClick = () => {
    console.log("===== FULL TRIAL OBJECT =====");
    console.log(trial);
    console.log("===== TRIAL KEYS =====");
    console.log(Object.keys(trial));
    console.log("=============================");
    setExpanded(!expanded);
  };

  const handleEdit = () => {
    navigate(`/admin/trials/edit/${trial.id}`);
  };

  const handleBookTravel = () => {
    console.log('🔍 Full trial object:', trial);
    console.log('🔍 Trial locations:', trial.locations);
    
    const location = getBestTrialLocation(trial);
    
    // Extract location details from trial.locations
    let locationDetails = {
      facility: '',
      city: '',
      state: '',
      address: ''
    };
    
    if (trial.locations && trial.locations.length > 0) {
      const firstLocation = trial.locations[0];
      console.log('🔍 First location:', firstLocation);
      
      // Handle both string and object locations
      if (typeof firstLocation === 'object') {
        locationDetails.facility = firstLocation.facility || firstLocation.name || '';
        locationDetails.city = firstLocation.city || '';
        locationDetails.state = firstLocation.state || '';
        locationDetails.address = firstLocation.address || '';
      } else if (typeof firstLocation === 'string') {
        // If it's a string, try to parse it
        locationDetails.city = firstLocation;
      }
    }
    
    console.log('🔍 Extracted location details:', locationDetails);
    
    // Navigate to NephroTrax travel booking with trial data
    navigate('/travel/book', {
      state: {
        trial: {
          nctId: trial.nctId || trial.id || '',
          officialTitle: trial.title || trial.officialTitle || '',
          facility: locationDetails.facility,
          city: locationDetails.city,
          state: locationDetails.state,
          address: locationDetails.address,
        }
      }
    });
  };

  const SourceBadge = () => {
    if (trial.source === "external") {
      return (
        <Chip
          icon={<PublicIcon />}
          label="ClinicalTrials.gov"
          size="small"
          color="primary"
          variant="outlined"
        />
      );
    }
    return (
      <Chip
        icon={<StorageIcon />}
        label="Our Database"
        size="small"
        color="success"
        variant="outlined"
      />
    );
  };

  const borderColor = trial.source === "external" ? "#1976d2" : "#2e7d32";
  const hasLocation = getBestTrialLocation(trial) !== null;

  return (
    <Card
      elevation={3}
      sx={{
        "&:hover": { boxShadow: 6 },
        border: `2px solid ${borderColor}`,
      }}
    >
      <CardContent>
        {/* Source Badge and Edit Button */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <SourceBadge />

          {/* Only show edit button for internal trials */}
          {trial.source === "internal" && trial.id && (
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={handleEdit}
              startIcon={<EditIcon />}
            >
              Edit
            </Button>
          )}
        </Box>

        {/* Trial Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "start",
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h3" sx={{ flex: 1, pr: 2 }}>
            {trial.title}
          </Typography>
          <Chip
            label={formatStatus(trial.status)}
            color={getStatusColor(trial.status)}
            size="small"
          />
        </Box>

        {/* NCT ID */}
        <Typography
          variant="body2"
          color="primary"
          sx={{ mb: 2, fontWeight: "bold" }}
        >
          NCT ID: {trial.nctId}
        </Typography>

        {/* Trial Info Grid */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ScienceIcon fontSize="small" color="action" />
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Phase
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {trial.phase || "N/A"}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {trial.sponsor && (
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon fontSize="small" color="action" />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Sponsor
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {trial.sponsor}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {(trial.ageRange || (trial.minAge && trial.maxAge)) && (
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CalendarTodayIcon fontSize="small" color="action" />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Age Range
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {trial.ageRange ||
                      `${trial.minAge} - ${trial.maxAge}` ||
                      "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}

          {trial.locations && (
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <LocationOnIcon fontSize="small" color="action" />
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Locations
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {Array.isArray(trial.locations)
                      ? `${trial.locations.length} site(s)`
                      : trial.locations}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Brief Summary or Description */}
        {(trial.briefSummary || trial.description) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              {(trial.briefSummary || trial.description).length > 300
                ? `${(trial.briefSummary || trial.description).substring(
                    0,
                    300
                  )}...`
                : trial.briefSummary || trial.description}
            </Typography>
          </>
        )}

        {/* Additional Internal Trial Info */}
        {trial.source === "internal" && (
          <>
            {trial.ckdStage && (
              <Box sx={{ mt: 2 }}>
                <Chip
                  label={`Stage: ${trial.ckdStage}`}
                  size="small"
                  color="primary"
                  sx={{ mr: 1 }}
                />
                {trial.targetOrgan && (
                  <Chip
                    label={`Target: ${trial.targetOrgan}`}
                    size="small"
                    color="secondary"
                  />
                )}
              </Box>
            )}
          </>
        )}

        {/* Locations for External Trials */}
        {trial.source === "external" &&
          trial.locations &&
          Array.isArray(trial.locations) &&
          trial.locations.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Trial Locations:
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {trial.locations.slice(0, 3).map((location, index) => (
                  <Chip
                    key={index}
                    icon={<LocationOnIcon />}
                    label={location}
                    size="small"
                    variant="outlined"
                  />
                ))}
                {trial.locations.length > 3 && (
                  <Chip
                    label={`+${trial.locations.length - 3} more`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </>
          )}
      </CardContent>

      {/* Card Actions - View Details + Book Travel */}
      <CardActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleExpandClick}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          fullWidth
        >
          {expanded ? "Hide Details" : "View Details"}
        </Button>

        {/* Book Travel Button */}
        <Button
          size="small"
          variant="contained"
          onClick={handleBookTravel}
          startIcon={<FlightTakeoffIcon />}
          fullWidth
          sx={{
            background: "linear-gradient(135deg, #0f3d6e 0%, #1B6CA8 100%)",
            color: "white",
            fontWeight: "bold",
            "&:hover": {
              background: "linear-gradient(135deg, #0a2d52 0%, #155a8a 100%)",
            },
          }}
        >
          Book Travel
        </Button>
      </CardActions>

      {/* Expandable Details Section */}
      {/* Expandable Details Section */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider />
        <CardContent sx={{ bgcolor: "#f5f5f5" }}>
          <Grid container spacing={3}>
            {/* Detailed Description */}
            {trial.detailedDescription && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Detailed Description
                </Typography>
                <Paper elevation={0} sx={{ p: 2, bgcolor: "white" }}>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                    {trial.detailedDescription}
                  </Typography>
                </Paper>
              </Grid>
            )}

            {/* Conditions */}
            {(trial.conditions || trial.condition) && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Conditions Studied
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {Array.isArray(trial.conditions) ? (
                    trial.conditions.map((condition, idx) => (
                      <Chip key={idx} label={condition} color="info" />
                    ))
                  ) : (
                    <Chip label={trial.condition || trial.conditions} color="info" />
                  )}
                </Box>
              </Grid>
            )}

            {/* Interventions/Arms */}
            {trial.interventions && Array.isArray(trial.interventions) && trial.interventions.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Interventions
                </Typography>
                <Grid container spacing={2}>
                  {trial.interventions.map((intervention, idx) => (
                    <Grid item xs={12} sm={6} key={idx}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: "white" }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          {intervention.type || "Intervention"}
                        </Typography>
                        <Typography variant="body2" fontWeight="bold">
                          {intervention.name}
                        </Typography>
                        {intervention.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {intervention.description}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            {/* Study Arms */}
            {trial.arms && Array.isArray(trial.arms) && trial.arms.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Study Arms
                </Typography>
                <Grid container spacing={2}>
                  {trial.arms.map((arm, idx) => (
                    <Grid item xs={12} key={idx}>
                      <Paper elevation={0} sx={{ p: 2, bgcolor: "white" }}>
                        <Typography variant="subtitle2" color="secondary" gutterBottom>
                          {arm.label || `Arm ${idx + 1}`} {arm.type && `(${arm.type})`}
                        </Typography>
                        {arm.description && (
                          <Typography variant="body2">{arm.description}</Typography>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}

            {/* Outcome Measures */}
            {(trial.primaryOutcome || trial.secondaryOutcome || trial.outcomeMeasures) && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Outcome Measures
                </Typography>
                {trial.primaryOutcome && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="success.main" gutterBottom>
                      Primary Outcomes:
                    </Typography>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: "white" }}>
                      <Typography variant="body2" component="div" sx={{ whiteSpace: "pre-line" }}>
                        {Array.isArray(trial.primaryOutcome)
                          ? trial.primaryOutcome.map((outcome, idx) => (
                              <Box key={idx} sx={{ mb: 1 }}>
                                • {typeof outcome === "string" ? outcome : outcome.measure}
                                {outcome.timeFrame && ` [${outcome.timeFrame}]`}
                              </Box>
                            ))
                          : trial.primaryOutcome}
                      </Typography>
                    </Paper>
                  </Box>
                )}
                {trial.secondaryOutcome && (
                  <Box>
                    <Typography variant="subtitle2" color="info.main" gutterBottom>
                      Secondary Outcomes:
                    </Typography>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: "white" }}>
                      <Typography variant="body2" component="div" sx={{ whiteSpace: "pre-line" }}>
                        {Array.isArray(trial.secondaryOutcome)
                          ? trial.secondaryOutcome.map((outcome, idx) => (
                              <Box key={idx} sx={{ mb: 1 }}>
                                • {typeof outcome === "string" ? outcome : outcome.measure}
                                {outcome.timeFrame && ` [${outcome.timeFrame}]`}
                              </Box>
                            ))
                          : trial.secondaryOutcome}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Grid>
            )}

            {/* ── Eligibility Criteria + Plain English — SIDE BY SIDE ────────── */}
          {/* ── Eligibility Criteria + Plain English — SIDE BY SIDE ────────── */}
{(trial.eligibilityCriteria || trial.inclusionCriteria || trial.exclusionCriteria || trial.id) && (
  <>
    <Grid item xs={12}>
      <Divider />
    </Grid>

    {/* Left — Raw Eligibility Criteria */}
    {(trial.eligibilityCriteria || trial.inclusionCriteria || trial.exclusionCriteria) && (
      <Grid item xs={12} md={trial.id ? 6 : 12}>
        <Typography variant="h6" gutterBottom color="primary">
          Eligibility Criteria
        </Typography>
        <Paper elevation={0} sx={{ p: 2, bgcolor: "white", height: "100%", minHeight: 200 }}>
          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            {trial.eligibilityCriteria ||
              (trial.inclusionCriteria ? `Inclusion Criteria:\n${trial.inclusionCriteria}\n\n` : '') +
              (trial.exclusionCriteria ? `Exclusion Criteria:\n${trial.exclusionCriteria}` : '')}
          </Typography>
        </Paper>
      </Grid>
    )}

    {/* Right — Plain English Translation */}
    {trial.id && (
      <Grid item xs={12} md={(trial.eligibilityCriteria || trial.inclusionCriteria || trial.exclusionCriteria) ? 6 : 12}>
        <Typography variant="h6" gutterBottom color="primary">
          🤝Do I Qualify? — Plain English
        </Typography>
        <Paper
          elevation={0}
          sx={{
            p: 2,
            bgcolor: "#f8faff",
            border: "1px solid #e0e7ff",
            borderRadius: 2,
            height: "100%",
            minHeight: 200,
          }}
        >
          <EligibilityPlainText
            trialId={trial.id}
            nctId={trial.nctId}
            trialName={trial.title || trial.officialTitle}
            defaultOpen={true}
            compact={true}
          />
        </Paper>
      </Grid>
    )}
  </>
)}

            {/* Study Design Details */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom color="primary">
                Study Design & Information
              </Typography>
              <Grid container spacing={2}>
                {trial.studyType && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Study Type:</strong> {trial.studyType}
                    </Typography>
                  </Grid>
                )}
                {trial.enrollment && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Enrollment:</strong> {trial.enrollment}
                    </Typography>
                  </Grid>
                )}
                {trial.startDate && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Start Date:</strong> {trial.startDate}
                    </Typography>
                  </Grid>
                )}
                {trial.completionDate && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Completion Date:</strong> {trial.completionDate}
                    </Typography>
                  </Grid>
                )}
                {trial.gender && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Gender:</strong> {trial.gender}
                    </Typography>
                  </Grid>
                )}
                {trial.acceptsHealthyVolunteers !== undefined && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Healthy Volunteers:</strong>{" "}
                      {trial.acceptsHealthyVolunteers ? "Yes" : "No"}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>

            {/* Keywords */}
            {trial.keywords && Array.isArray(trial.keywords) && trial.keywords.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Keywords
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                  {trial.keywords.map((keyword, idx) => (
                    <Chip key={idx} label={keyword} size="small" variant="outlined" />
                  ))}
                </Box>
              </Grid>
            )}

            {/* Action Buttons */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: "flex", gap: 2 }}>
                {trial.source === "internal" && trial.id && (
                  <Button variant="contained" color="success" onClick={handleEdit}
                    startIcon={<EditIcon />} fullWidth>
                    Edit Trial Data
                  </Button>
                )}
                {trial.nctId && (
                  <Button
                    variant="contained"
                    color={trial.source === "external" ? "primary" : "success"}
                    href={`https://clinicaltrials.gov/study/${trial.nctId}`}
                    target="_blank" rel="noopener noreferrer"
                    endIcon={<OpenInNewIcon />} fullWidth>
                    View on ClinicalTrials.gov
                  </Button>
                )}
                <Button variant="contained" onClick={handleBookTravel}
                  startIcon={<FlightTakeoffIcon />} fullWidth
                  sx={{
                    background: "linear-gradient(135deg, #0f3d6e 0%, #1B6CA8 100%)",
                    color: "white", fontWeight: "bold",
                    "&:hover": { background: "linear-gradient(135deg, #0a2d52 0%, #155a8a 100%)" },
                  }}>
                  Book Travel via SanoGrid
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default ClinicalTrialsSearch;